import { useEffect, RefObject } from "react";
import { useRouter } from "next/router";

// The quiz share link is a `?module=quiz` deep link. Inside a Digital Exhibit
// the DX template resolves that param and scrolls to the module; on other pages
// (default theme) no such handler exists, so the quiz resolves its own deep link
// and scrolls itself into view.
export function useQuizDeepLink(rootRef: RefObject<HTMLElement>, enabled: boolean, reduceMotion: boolean) {
  const router = useRouter();
  const active = router.query?.module === "quiz";

  useEffect(() => {
    if (!enabled || !active) return;
    const el = rootRef.current;
    if (!el) return;

    const headerOffset = () => document.querySelector("header")?.clientHeight ?? 0;
    const scrollToQuiz = () => {
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset();
      window.scrollTo({ top, behavior: reduceMotion ? "auto" : "smooth" });
    };

    // Wait two frames for layout to settle, then scroll; correct once if a late
    // layout shift (e.g. hero media above) leaves the module off-target.
    const raf = requestAnimationFrame(() => requestAnimationFrame(scrollToQuiz));
    const correct = window.setTimeout(() => {
      if (Math.abs(el.getBoundingClientRect().top - headerOffset()) > 8) scrollToQuiz();
    }, 700);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(correct);
    };
  }, [enabled, active, reduceMotion, rootRef]);
}
